import EditableList from '@/components/EditableList'
import RelayCard from '@/components/RelayCard'
import { useFetchProfile } from '@/hooks/useFetchProfile'
import { normalizeUrl } from '@/lib/url'
import { username } from '@/lib/event-metadata'
import SecondaryPageLayout from '@/layouts/SecondaryPageLayout'
import { useAtomValue } from 'jotai'
import { invalidateBlockedRelays, blockedRelaysChannelAtom } from '@/atoms/invalidation'
import client from '@/services/client.service'
import { loadBlockedRelays } from '@nostr/gadgets/lists'
import { unwrappedBlockedRelaysAtom } from '@/atoms/lists'
import * as kinds from '@nostr/tools/kinds'
import { now } from '@/lib/utils'
import { forwardRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useAsync } from 'react-use'
import { useCurrentPubkey } from '@/atoms/session'

const BlockedRelaysPage = forwardRef(({ id, index }: { id?: string; index?: number }, ref) => {
  const { t } = useTranslation()
  const currentPubkey = useCurrentPubkey()
  const { profile } = useFetchProfile(id)
  const channel = useAtomValue(blockedRelaysChannelAtom)
  const ourBlockedRelays = useAtomValue(unwrappedBlockedRelaysAtom) ?? []

  useEffect(() => {
    if (!profile) return
    const name = profile.shortName || username(profile) || '...'
    document.title = t("{{name}}'s blocked relays on {{appName}}", {
      name,
      appName: window.hallway.universe.appName
    })
    history.replaceState(null, '')
  }, [profile])
  const { value: relays } = useAsync(async () => {
    if (!profile) return []
    if (profile.pubkey === currentPubkey) return ourBlockedRelays
    return (await loadBlockedRelays(profile.pubkey)).items
  }, [profile?.pubkey, channel, ourBlockedRelays])

  return (
    <SecondaryPageLayout
      ref={ref}
      index={index}
      title={
        profile && profile.pubkey !== currentPubkey
          ? t("{{username}}'s blocked relays", { username: username(profile) })
          : t('Blocked Relays')
      }
    >
      <div className="px-4 py-3">
        <EditableList
          items={relays || []}
          editable={profile?.pubkey === currentPubkey}
          renderItem={(url) => <RelayCard url={url} />}
          validateItem={(val: string) => {
            if (!currentPubkey) return { ok: false as const, error: t('Login required') }
            const normalized = normalizeUrl(val)
            if (!normalized) return { ok: false as const, error: t('Invalid relay URL') }
            if ((relays || []).includes(normalized))
              return { ok: false as const, error: t('Relay already exists') }
            return { ok: true as const, item: normalized }
          }}
          onUpdate={async (newRelays: string[]) => {
            if (!currentPubkey) return
            const event = await client.publish({
              kind: kinds.BlockedRelaysList,
              content: '',
              tags: newRelays.map((url) => ['relay', url]),
              created_at: now()
            })
            if (!event) {
              console.warn('failed to publish updated blocked relays list')
              toast.error(t('Failed to publish updated blocked relays list.'))
              return
            }
            await loadBlockedRelays(event.pubkey, undefined, event)
            invalidateBlockedRelays()
          }}
          placeholder={t('Enter relay URL')}
          emptyMessage={t('No blocked relays.')}
        />
      </div>
    </SecondaryPageLayout>
  )
})
BlockedRelaysPage.displayName = 'BlockedRelaysPage'
export default BlockedRelaysPage
